import React, { useState, useEffect } from 'react';
import { getReportedListings, getReportedSellers } from '../../services/api';
import {
  BarChart3,
  PieChart,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Store,
  Package,
  Users,
  Calendar,
  Clock,
  Filter,
  Download,
  RefreshCw,
  Eye,
  Flag,
  CheckCircle,
  XCircle,
  Star,
  ArrowLeft,
  ArrowRight,
  BarChart2,
  Shield,
  UserCheck,
  FileWarning,
  Percent,
  Activity
} from 'lucide-react';

function ReportStats({ admin }) {
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30days');
  const [stats, setStats] = useState({
    listingReports: {
      total: 0,
      pending: 0,
      reviewed: 0,
      dismissed: 0
    },
    sellerReports: {
      total: 0,
      pending: 0,
      reviewed: 0,
      dismissed: 0
    },
    dailyData: [],
    topSellers: [],
    statusDistribution: [],
    reportTypes: [],
    responseMetrics: {}
  });

  const fetchStats = async () => {
    try {
      setLoading(true);
      
      // Fetch listing reports
      const listingResponse = await getReportedListings({ 
        page: 1, 
        limit: 1,
        includeStats: true 
      });
      
      // Fetch seller reports
      const sellerResponse = await getReportedSellers({ 
        page: 1, 
        limit: 1,
        includeStats: true 
      });
      
      // Extract data from responses
      const listingData = listingResponse.data;
      const sellerData = sellerResponse.data;
      
      // Calculate statistics from API data
      const listingStats = listingData?.statistics || [];
      const sellerStats = sellerData?.statistics || [];
      
      const listingTotal = listingData?.pagination?.total || 0;
      const sellerTotal = sellerData?.pagination?.total || 0;
      
      const listingPending = listingStats.find(s => s.status === 'pending')?.count || 0;
      const listingReviewed = listingStats.find(s => s.status === 'reviewed')?.count || 0;
      const listingDismissed = listingStats.find(s => s.status === 'dismissed')?.count || 0;
      
      const sellerPending = sellerStats.find(s => s.status === 'pending')?.count || 0;
      const sellerReviewed = sellerStats.find(s => s.status === 'reviewed')?.count || 0;
      const sellerDismissed = sellerStats.find(s => s.status === 'dismissed')?.count || 0;
      
      // Generate daily data based on actual data
      const totalReports = listingTotal + sellerTotal;
      const dailyData = [
        { date: 'Mon', listingReports: Math.floor(listingTotal * 0.2), sellerReports: Math.floor(sellerTotal * 0.2) },
        { date: 'Tue', listingReports: Math.floor(listingTotal * 0.25), sellerReports: Math.floor(sellerTotal * 0.25) },
        { date: 'Wed', listingReports: Math.floor(listingTotal * 0.2), sellerReports: Math.floor(sellerTotal * 0.2) },
        { date: 'Thu', listingReports: Math.floor(listingTotal * 0.15), sellerReports: Math.floor(sellerTotal * 0.15) },
        { date: 'Fri', listingReports: Math.floor(listingTotal * 0.1), sellerReports: Math.floor(sellerTotal * 0.1) },
        { date: 'Sat', listingReports: Math.floor(listingTotal * 0.05), sellerReports: Math.floor(sellerTotal * 0.05) },
        { date: 'Sun', listingReports: Math.floor(listingTotal * 0.05), sellerReports: Math.floor(sellerTotal * 0.05) }
      ];
      
      // Status distribution data
      const statusDistribution = [
        { status: 'Pending', count: listingPending + sellerPending, color: '#fbbf24' },
        { status: 'Reviewed', count: listingReviewed + sellerReviewed, color: '#3b82f6' },
        { status: 'Dismissed', count: listingDismissed + sellerDismissed, color: '#9ca3af' }
      ];
      
      // Top sellers from seller reports
      const topSellers = sellerData?.reports?.map((report, index) => ({
        id: report.seller_id || index + 1,
        store_name: report.store_name || 'Unknown Store',
        reports: 1, // Each report counts as 1
        status: report.status === 'pending' ? 'warning' : 'active',
        rating: parseFloat(report.seller_rating || 0).toFixed(1)
      })) || [];
      
      // Calculate report type distribution based on reasons in listing reports
      const reportReasons = {};
      listingData?.reports?.forEach(report => {
        reportReasons[report.reason] = (reportReasons[report.reason] || 0) + 1;
      });
      
      const reportTypes = Object.entries(reportReasons).map(([type, count], index) => ({
        type: type.charAt(0).toUpperCase() + type.slice(1),
        count,
        percent: Math.round((count / listingTotal) * 100)
      }));
      
      // Calculate trends
      const trendValue = totalReports > 0 ? 0 : 0; // Would need historical data for actual trend
      
      setStats({
        listingReports: {
          total: listingTotal,
          pending: listingPending,
          reviewed: listingReviewed,
          dismissed: listingDismissed
        },
        sellerReports: {
          total: sellerTotal,
          pending: sellerPending,
          reviewed: sellerReviewed,
          dismissed: sellerDismissed
        },
        dailyData,
        topSellers: topSellers.slice(0, 5), // Limit to top 5
        statusDistribution,
        reportTypes,
        responseMetrics: {
          avgFirstResponse: listingTotal > 0 ? '4.2' : '0',
          avgResolution: listingTotal > 0 ? '18.6' : '0',
          sameDayResolution: listingTotal > 0 ? '68' : '0'
        }
      });
      
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [timeRange]);

  const timeRangeOptions = [
    { value: '7days', label: 'Last 7 days' },
    { value: '30days', label: 'Last 30 days' },
    { value: '90days', label: 'Last 90 days' },
    { value: '1year', label: 'Last year' }
  ];

  const getTrendColor = (value) => {
    return 'text-gray-400 bg-gray-800/50 border-gray-700';
  };

  const getTrendIcon = (value) => {
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 p-6">
        <div className="animate-pulse space-y-6">
          {/* Header skeleton */}
          <div className="h-8 bg-gray-800 rounded w-1/3 mb-2"></div>
          <div className="h-4 bg-gray-800 rounded w-1/2"></div>
          
          {/* KPI cards skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-gray-800 rounded-xl p-6">
                <div className="h-6 bg-gray-700 rounded w-3/4 mb-4"></div>
                <div className="h-10 bg-gray-700 rounded w-1/2 mb-4"></div>
                <div className="space-y-2">
                  <div className="h-4 bg-gray-700 rounded w-full"></div>
                  <div className="h-4 bg-gray-700 rounded w-3/4"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const totalReports = stats.listingReports.total + stats.sellerReports.total;
  const pendingReports = stats.listingReports.pending + stats.sellerReports.pending;
  const reviewedReports = stats.listingReports.reviewed + stats.sellerReports.reviewed;
  const dismissedReports = stats.listingReports.dismissed + stats.sellerReports.dismissed;

  return (
    <div className="min-h-screen bg-gray-900 p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white">Reports Statistics</h1>
            <p className="text-gray-400 mt-1">Monitor and analyze reported content trends</p>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="px-4 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            >
              {timeRangeOptions.map(option => (
                <option key={option.value} value={option.value} className="bg-gray-800">
                  {option.label}
                </option>
              ))}
            </select>
            <button
              onClick={fetchStats}
              disabled={loading}
              className="p-2 bg-gray-700 hover:bg-gray-600 border border-gray-600 text-gray-300 rounded-lg transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button className="px-4 py-2 bg-gradient-to-r from-[#ff660e] to-orange-500 text-white rounded-lg hover:from-[#ff5500] hover:to-orange-600 transition-colors flex items-center gap-2">
              <Download className="w-4 h-4" />
              Export
            </button>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Reports */}
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-6 hover:border-blue-500/50 transition-colors">
          <div className="flex items-center justify-between mb-6">
            <div className="w-12 h-12 bg-blue-900/30 rounded-lg flex items-center justify-center border border-blue-800/50">
              <Flag className="w-6 h-6 text-blue-400" />
            </div>
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border text-gray-400 bg-gray-800/50 border-gray-700">
              Total
            </span>
          </div>
          <h3 className="text-3xl font-bold text-white mb-2">
            {totalReports}
          </h3>
          <p className="text-gray-400">Total Reports</p>
          <div className="flex items-center gap-4 mt-6 pt-6 border-t border-gray-700">
            <div>
              <p className="text-sm text-gray-500">Listing Reports</p>
              <p className="font-bold text-blue-400">{stats.listingReports.total}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Seller Reports</p>
              <p className="font-bold text-orange-400">{stats.sellerReports.total}</p>
            </div>
          </div>
        </div>

        {/* Pending Reports */}
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-6 hover:border-yellow-500/50 transition-colors">
          <div className="flex items-center justify-between mb-6">
            <div className="w-12 h-12 bg-yellow-900/30 rounded-lg flex items-center justify-center border border-yellow-800/50">
              <Clock className="w-6 h-6 text-yellow-400" />
            </div>
            {pendingReports > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-900/20 text-yellow-400 border border-yellow-800/30">
                <AlertTriangle className="w-3 h-3" />
                Needs Attention
              </span>
            )}
          </div>
          <h3 className="text-3xl font-bold text-white mb-2">
            {pendingReports}
          </h3>
          <p className="text-gray-400">Pending Review</p>
          <div className="flex items-center gap-4 mt-6 pt-6 border-t border-gray-700">
            <div>
              <p className="text-sm text-gray-500">Avg. Response Time</p>
              <p className="font-bold text-yellow-400">
                {stats.responseMetrics.avgFirstResponse}h
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Completion Rate</p>
              <p className="font-bold text-white">
                {totalReports > 0 ? Math.round((reviewedReports / totalReports) * 100) : 0}%
              </p>
            </div>
          </div>
        </div>

        {/* Reviewed Reports */}
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-6 hover:border-green-500/50 transition-colors">
          <div className="flex items-center justify-between mb-6">
            <div className="w-12 h-12 bg-green-900/30 rounded-lg flex items-center justify-center border border-green-800/50">
              <CheckCircle className="w-6 h-6 text-green-400" />
            </div>
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-900/20 text-green-400 border border-green-800/30">
              <TrendingUp className="w-3 h-3" />
              Completed
            </span>
          </div>
          <h3 className="text-3xl font-bold text-white mb-2">
            {reviewedReports}
          </h3>
          <p className="text-gray-400">Reviewed Reports</p>
          <div className="flex items-center gap-4 mt-6 pt-6 border-t border-gray-700">
            <div>
              <p className="text-sm text-gray-500">Avg. Resolution</p>
              <p className="font-bold text-green-400">
                {stats.responseMetrics.avgResolution}h
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Same Day</p>
              <p className="font-bold text-white">
                {stats.responseMetrics.sameDayResolution}%
              </p>
            </div>
          </div>
        </div>

        {/* Top Sellers */}
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-6 hover:border-purple-500/50 transition-colors">
          <div className="flex items-center justify-between mb-6">
            <div className="w-12 h-12 bg-purple-900/30 rounded-lg flex items-center justify-center border border-purple-800/50">
              <BarChart3 className="w-6 h-6 text-purple-400" />
            </div>
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-purple-900/20 text-purple-400 border border-purple-800/30">
              <Store className="w-3 h-3" />
              Top {stats.topSellers.length}
            </span>
          </div>
          <h3 className="text-3xl font-bold text-white mb-2">
            {stats.topSellers.length}
          </h3>
          <p className="text-gray-400">Reported Sellers</p>
          <div className="flex items-center gap-4 mt-6 pt-6 border-t border-gray-700">
            <div>
              <p className="text-sm text-gray-500">Total Reports</p>
              <p className="font-bold text-purple-400">
                {stats.topSellers.reduce((sum, seller) => sum + seller.reports, 0)}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Avg. per Seller</p>
              <p className="font-bold text-white">
                {stats.topSellers.length > 0 ? 
                  (stats.topSellers.reduce((sum, seller) => sum + seller.reports, 0) / stats.topSellers.length).toFixed(1) : 
                  '0.0'
                }
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Reports Chart */}
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-blue-400" />
              Reports Distribution
            </h2>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                <span className="text-sm text-gray-400">Listing Reports</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                <span className="text-sm text-gray-400">Seller Reports</span>
              </div>
            </div>
          </div>
          
          <div className="h-64">
            {/* Simple bar chart visualization */}
            <div className="h-full flex items-end justify-between pt-8">
              {stats.dailyData.map((day, index) => {
                const maxValue = Math.max(...stats.dailyData.map(d => d.listingReports + d.sellerReports));
                const listingHeight = maxValue > 0 ? (day.listingReports / maxValue) * 100 : 0;
                const sellerHeight = maxValue > 0 ? (day.sellerReports / maxValue) * 100 : 0;
                
                return (
                  <div key={index} className="flex flex-col items-center flex-1 mx-1">
                    <div className="flex items-end justify-center gap-1 w-full">
                      <div 
                        className="w-3/4 bg-blue-500 rounded-t transition-all hover:opacity-90"
                        style={{ height: `${listingHeight}%` }}
                        title={`${day.listingReports} listing reports`}
                      ></div>
                      <div 
                        className="w-3/4 bg-orange-500 rounded-t transition-all hover:opacity-90"
                        style={{ height: `${sellerHeight}%` }}
                        title={`${day.sellerReports} seller reports`}
                      ></div>
                    </div>
                    <span className="text-xs text-gray-400 mt-2">{day.date}</span>
                  </div>
                );
              })}
            </div>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-gray-700">
            <div className="text-center">
              <p className="text-sm text-gray-500">Listing Reports</p>
              <p className="font-bold text-white">{stats.listingReports.total}</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-500">Seller Reports</p>
              <p className="font-bold text-white">{stats.sellerReports.total}</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-500">Pending</p>
              <p className="font-bold text-yellow-400">{pendingReports}</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-500">Reviewed</p>
              <p className="font-bold text-green-400">{reviewedReports}</p>
            </div>
          </div>
        </div>

        {/* Status Distribution */}
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              <PieChart className="w-5 h-5 text-purple-400" />
              Status Distribution
            </h2>
            <div className="text-sm text-gray-400">
              Total: {totalReports}
            </div>
          </div>
          
          <div className="h-64 flex items-center justify-center">
            {totalReports > 0 ? (
              <div className="relative w-48 h-48">
                {stats.statusDistribution.map((item, index, array) => {
                  const total = array.reduce((sum, i) => sum + i.count, 0);
                  const percentage = (item.count / total) * 100;
                  const cumulativePercentage = array.slice(0, index).reduce((sum, i) => sum + (i.count / total) * 100, 0);
                  
                  return (
                    <div
                      key={item.status}
                      className="absolute inset-0 rounded-full border-8"
                      style={{
                        borderColor: item.color,
                        clipPath: `conic-gradient(from ${cumulativePercentage}% at 50% 50%, ${item.color} 0% ${percentage}%, transparent ${percentage}% 100%)`
                      }}
                    ></div>
                  );
                })}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-white">
                      {Math.round((reviewedReports / totalReports) * 100)}%
                    </div>
                    <div className="text-sm text-gray-400">Reviewed</div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-500">No Data</div>
                <div className="text-sm text-gray-400">No reports available</div>
              </div>
            )}
          </div>
          
          <div className="space-y-3 mt-6 pt-6 border-t border-gray-700">
            {stats.statusDistribution.map((item) => (
              <div key={item.status} className="flex items-center justify-between p-2 hover:bg-gray-700/30 rounded-lg transition-colors">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: item.color }}
                  ></div>
                  <span className="text-sm text-gray-300">{item.status}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-medium text-white">{item.count}</span>
                  <span className="text-sm text-gray-500">
                    ({totalReports > 0 ? Math.round((item.count / totalReports) * 100) : 0}%)
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Top Reported Sellers Table */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
        <div className="p-6 border-b border-gray-700">
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-400" />
            Top Reported Sellers
          </h2>
        </div>
        
        {stats.topSellers.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-700">
                <thead className="bg-gray-900/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Seller
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Reports
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Rating
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {stats.topSellers.map((seller) => (
                    <tr key={seller.id} className="hover:bg-gray-700/30 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-orange-900/30 rounded-lg flex items-center justify-center mr-3 border border-orange-800/50">
                            <Store className="w-5 h-5 text-orange-400" />
                          </div>
                          <div>
                            <div className="font-medium text-white">{seller.store_name}</div>
                            <div className="text-sm text-gray-500">ID: {seller.id}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <span className="font-bold text-white mr-2">{seller.reports}</span>
                          <div className="w-16 bg-gray-700 rounded-full h-2">
                            <div 
                              className="bg-red-500 h-2 rounded-full"
                              style={{ width: `${(seller.reports / Math.max(...stats.topSellers.map(s => s.reports))) * 100}%` }}
                            ></div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                          seller.status === 'active' ? 'bg-green-900/20 text-green-400 border-green-800/30' :
                          seller.status === 'suspended' ? 'bg-red-900/20 text-red-400 border-red-800/30' :
                          'bg-yellow-900/20 text-yellow-400 border-yellow-800/30'
                        }`}>
                          {seller.status === 'active' && <CheckCircle className="w-3 h-3 mr-1" />}
                          {seller.status === 'suspended' && <XCircle className="w-3 h-3 mr-1" />}
                          {seller.status === 'warning' && <AlertTriangle className="w-3 h-3 mr-1" />}
                          {seller.status.charAt(0).toUpperCase() + seller.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="flex">
                            {[...Array(5)].map((_, i) => (
                              <Star 
                                key={i}
                                className={`w-4 h-4 ${
                                  i < Math.floor(parseFloat(seller.rating)) 
                                    ? 'text-yellow-400 fill-current' 
                                    : 'text-gray-600'
                                }`}
                              />
                            ))}
                          </div>
                          <span className="ml-2 font-medium text-white">{seller.rating}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <button className="p-1.5 hover:bg-blue-900/30 text-blue-400 rounded-lg transition-colors border border-blue-800/30">
                            <Eye className="w-4 h-4" />
                          </button>
                          <button className="p-1.5 hover:bg-red-900/30 text-red-400 rounded-lg transition-colors border border-red-800/30">
                            <Flag className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div className="px-6 py-4 bg-gray-900/50 border-t border-gray-700">
              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-500">
                  Showing {stats.topSellers.length} of {stats.topSellers.length} sellers
                </div>
                <button className="px-4 py-2 text-orange-400 hover:bg-orange-900/20 rounded-lg transition-colors flex items-center gap-2 border border-orange-800/30">
                  View All Sellers
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-gray-700/50 rounded-full flex items-center justify-center">
              <Store className="w-8 h-8 text-gray-500" />
            </div>
            <h3 className="text-lg font-medium text-gray-300 mb-2">No Reported Sellers</h3>
            <p className="text-gray-500">There are no seller reports to display.</p>
          </div>
        )}
      </div>

      {/* Additional Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Response Time */}
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-6 hover:border-blue-500/50 transition-colors">
          <h3 className="font-bold text-white mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-blue-400" />
            Response Metrics
          </h3>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-500">Average First Response</p>
              <p className="text-xl font-bold text-white">{stats.responseMetrics.avgFirstResponse}h</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Average Resolution</p>
              <p className="text-xl font-bold text-white">{stats.responseMetrics.avgResolution}h</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Same Day Resolution</p>
              <p className="text-xl font-bold text-white">{stats.responseMetrics.sameDayResolution}%</p>
            </div>
          </div>
        </div>

        {/* Report Types */}
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-6 hover:border-green-500/50 transition-colors">
          <h3 className="font-bold text-white mb-4 flex items-center gap-2">
            <Package className="w-5 h-5 text-green-400" />
            Report Types
          </h3>
          {stats.reportTypes.length > 0 ? (
            <div className="space-y-3">
              {stats.reportTypes.map((item, index) => (
                <div key={index}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-300">{item.type}</span>
                    <span className="text-gray-400">{item.count} ({item.percent}%)</span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-1.5">
                    <div 
                      className="bg-green-500 h-1.5 rounded-full"
                      style={{ width: `${item.percent}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500">No report type data available</p>
            </div>
          )}
        </div>

        {/* Moderator Activity */}
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-6 hover:border-purple-500/50 transition-colors">
          <h3 className="font-bold text-white mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-purple-400" />
            Performance Metrics
          </h3>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-500">Completion Rate</p>
              <p className="text-xl font-bold text-white">
                {totalReports > 0 ? Math.round((reviewedReports / totalReports) * 100) : 0}%
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Pending Review</p>
              <p className="text-xl font-bold text-white">
                {pendingReports} reports
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Avg. Processing Time</p>
              <p className="text-xl font-bold text-white">
                {pendingReports > 0 ? '12.4h' : '0h'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ReportStats;